import React, { useEffect, useState, FC } from 'react';
import styled from 'styled-components';
import { useSprings, animated } from 'react-spring/web.cjs';
import { useDrag } from 'react-use-gesture';

import defaultImage from 'images/PromoBg@2x.png';
import { isiOS, bannerAutoRotateInterval } from 'config';
import fsc from 'fsc';
import { Content } from './Content';
import { Banner } from '../../../types/banner';

const StyledSlider = styled.div`
  position: relative;
  height: 250px;
  margin-bottom: 20px;
`;

const BannerContainer = styled(animated.div)`
  position: absolute;
  height: 250px;
  width: 100vw;
  will-change: transform;
`;

const Dots = styled.div`
  position: absolute;
  left: 50%;
  bottom: -16px;
  z-index: 3;
  transform: translate3d(-50%, 0, 0);
`;

interface DotProps {
  active: boolean;
}
const Dot = styled.div<DotProps>`
  display: inline-block;
  width: ${isiOS ? 5 : 7}px;
  height: ${isiOS ? 5 : 7}px;
  border-radius: 50%;
  margin: 0 ${isiOS ? 3.5 : 3}px;
  background: ${({ active }: any) => active ? '#112530' : '#B8BEC1'};
`;

const BannerContainerBackground = styled.div`
  background: url('${({ backgroundUrl }: { backgroundUrl: string }) =>
  backgroundUrl ? backgroundUrl : defaultImage}');
  background-size: cover;
  filter: brightness(80%);
  height: 250px;
  left: 0;
  position: absolute;
  top: 0;
  width: 100vw;
`;

const getNextIndex = (index: number, length: number) =>
  index < 0 ? length + index : index >= length ? index - length : index;

interface ComputeStylesArgs {
  i: number;
  index: number;
  length: number;
  down: boolean;
  movement: number;
  forward: boolean;
}
const computeStyles = ({ i, index, length, down, movement, forward }: ComputeStylesArgs) => {
  const currentFirst = index === 0;
  const currentLast = index === length - 1;
  const isFirst = i === 0;
  const isLast = i === length - 1;
  const isCurrent = i === index;
  const isPrev = currentFirst ? isLast : i === index - 1;
  const isNext = currentLast ? isFirst : i === index + 1;

  let x;
  if (currentFirst && isLast) {
    x = -window.innerWidth;
  } else if (currentLast && isFirst) {
    x = window.innerWidth * (length - index);
  } else {
    x = window.innerWidth * (i - index);
  }

  x += down ? movement : 0;

  let visible = true;
  if (!(isPrev || isCurrent || isNext)) {
    visible = down;
  }

  let zIndex: string | number = 'initial';
  if (isCurrent) {
    zIndex = 2;
  } else if (forward ? isPrev : isNext) {
    zIndex = 1;
  }

  return {
    x,
    immediate: down,
    display: visible ? 'block' : 'none',
    zIndex
  };
};

interface BannerCarouselProps {
  fscViewName: string;
  banners: Banner[];
}

export const BannerCarousel: FC<BannerCarouselProps> = ({ fscViewName, banners }) => {
  const [current, setCurrent] = useState(0);
  const [isDown, setDown] = useState(false);
  const [props, set] = useSprings(banners.length, (i: number) => ({
    x: i * window.innerWidth,
    display: 'block',
    zIndex: 'initial'
  }));

  const sendFcsViewEvent = (bannerIndex: number, bannerUri: string, clicked = true) => {
    const text = `${clicked ? '' : 'Viewed '}Promo Action`;
    fsc
      .view(fscViewName)
      .Tapped(text)
      .withPayload({
        bannerIndex,
        bannerUri
      })
      .send();
  };

  useEffect(() => {
    let id: any;

    function startNextBannerTimeout() {
      id = setTimeout(() => {
        if (!isDown) {
          setCurrent((current) => {
            const newCurrent = getNextIndex(current + 1, banners.length);
            set((i: number) =>
              computeStyles({
                i,
                index: newCurrent,
                length: banners.length,
                down: isDown,
                movement: 0,
                forward: true
              })
            );
            return newCurrent;
          });
          startNextBannerTimeout();
        }
      }, bannerAutoRotateInterval);
    }

    startNextBannerTimeout();

    return () => clearTimeout(id);
  }, [isDown, current, banners.length, set]);

  const bind = useDrag(
    ({ down, movement: [mx], direction: [xDir], distance }: any) => {
      setDown(down);

      let currentIndex = current;
      if (!down && distance > window.innerWidth / 4) {
        setCurrent((current) => {
          const computedIndex = current + (xDir > 0 ? -1 : 1);
          currentIndex = getNextIndex(computedIndex, banners.length);
          return currentIndex;
        });

        sendFcsViewEvent(currentIndex, banners[currentIndex].background.uri, false);
      }

      set((i: number) =>
        computeStyles({
          i,
          index: currentIndex,
          length: banners.length,
          down,
          movement: mx,
          forward: xDir < 0
        })
      );
    }
  );

  return (
    <StyledSlider>
      {banners.map((banner, i) => (
        <BannerContainer
          {...bind()}
          key={i}
          style={{
            display: props[i].display,
            transform: props[i].x.interpolate(
              (x: number) => `translate3d(${x}px, 0, 0)`
            ),
            zIndex: props[i].zIndex
          }}
        >
          <BannerContainerBackground backgroundUrl={banner.background.uri} />
          <Content banner={banner} onClick={() => sendFcsViewEvent(i, banner.background.uri)}/> :
        </BannerContainer>
      ))}
      <Dots>
        {banners.map((_, i) => (
          <Dot key={i} active={i === current} />
        ))}
      </Dots>
    </StyledSlider>
  );
};
